import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Line
} from 'recharts';
import { getEventByIdAdmin } from '../../api/plan';
import { getTicketStats } from '../../api/subscription';

const TicketStatsComponent = ({ eventId }) => {
  const [stats, setStats] = useState({
    totalTickets: 0,
    totalRevenue: 0,
    totalInvitations: 0,
    totalCooperative: 0,
    cooperativeAmount: 0,
    viewers: 0,
    dailySales: [],
    monthlySales: [],
    voucherUsage: 0,
    paidTickets: 0,
    pendingTickets: 0,
    failedTickets: 0,
    totalDiscount: 0
  });
  // Breakdown by the event's own question; null when the organizer set none
  const [customField, setCustomField] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM format
  const [availableMonths, setAvailableMonths] = useState([]);



  useEffect(() => {
    const fetchStats = async () => {
      try {
        setChartLoading(true);
        
        // Fetch detailed ticket statistics from API
        const statsResponse = await getTicketStats(eventId, selectedMonth, 'month');
        const apiData = statsResponse?.data?.data;
        
        // Fetch event data for additional info
        const eventResponse = await getEventByIdAdmin(eventId);
        const eventData = eventResponse?.data?.data;
        
        if (apiData) {
          setCustomField(apiData.customField || null);
          setStats({
            totalTickets: apiData.stats?.totalTickets || 0,
            totalRevenue: apiData.stats?.totalRevenue || 0,
            totalInvitations: apiData.stats?.invitations || 0,
            totalCooperative: apiData.stats?.cooperative || 0,
            cooperativeAmount: apiData.stats?.cooperativeAmount || 0,
            viewers: eventData?.viewers || 0,
            dailySales: apiData.dailySales || [],
            monthlySales: apiData.monthlySales || [],
            voucherUsage: apiData.stats?.voucherUsage || 0,
            paidTickets: apiData.stats?.paidTickets || 0,
            pendingTickets: apiData.stats?.pendingTickets || 0,
            failedTickets: apiData.stats?.failedTickets || 0,
            totalDiscount: apiData.stats?.totalDiscount || 0,
            expectedGuests: apiData.stats?.expectedGuests || 0,
            enteredGuests: apiData.stats?.enteredGuests || 0
          });
          
          // Set available months
          if (apiData.availableMonths && apiData.availableMonths.length > 0) {
            setAvailableMonths(apiData.availableMonths);
            // Set the first available month if current month is not available
            if (!apiData.availableMonths.find(m => m.value === selectedMonth)) {
              setSelectedMonth(apiData.availableMonths[0].value);
            }
          }
        } else {
          // Fallback to basic event data if API fails
          setStats({
            totalTickets: eventData?.countAllPaidTickets || 0,
            totalRevenue: eventData?.totalPrice || 0,
            totalInvitations: eventData?.countAllInvitations || 0,
            viewers: eventData?.viewers || 0,
            dailySales: generateDailySalesData(eventData?.countAllPaidTickets || 0),
            monthlySales: generateDailySalesData(eventData?.countAllPaidTickets || 0),
            voucherUsage: 0,
            paidTickets: 0,
            pendingTickets: 0,
            failedTickets: 0,
            totalDiscount: 0
          });
        }

      } catch (error) {
        console.error('Error fetching ticket stats:', error);
        // Fallback to basic data
        try {
          const eventResponse = await getEventByIdAdmin(eventId);
          const eventData = eventResponse?.data?.data;
          
          setStats({
            totalTickets: eventData?.countAllPaidTickets || 0,
            totalRevenue: eventData?.totalPrice || 0,
            totalInvitations: eventData?.countAllInvitations || 0,
            viewers: eventData?.viewers || 0,
            dailySales: generateDailySalesData(eventData?.countAllPaidTickets || 0),
            monthlySales: generateDailySalesData(eventData?.countAllPaidTickets || 0),
            voucherUsage: 0,
            paidTickets: 0,
            pendingTickets: 0,
            failedTickets: 0,
            totalDiscount: 0
          });
        } catch (fallbackError) {
          console.error('Fallback error:', fallbackError);
        }
      } finally {
        setChartLoading(false);
      }
    };

    fetchStats();
  }, [eventId, selectedMonth]);

  // Generate mock daily sales data
  const generateDailySalesData = (totalTickets) => {
    const days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      
      // Generate realistic daily sales data
      const baseSales = Math.floor(totalTickets / 7);
      const variation = Math.floor(baseSales * 0.3 * (Math.random() - 0.5));
      const dailySales = Math.max(0, baseSales + variation);
      
      days.push({
        date: date.toLocaleDateString('az-AZ', { month: 'short', day: 'numeric' }),
        tickets: dailySales,
        revenue: dailySales * 25 // Mock average ticket price
      });
    }
    
    return days;
  };


  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Overview Cards - Always Static. The partner-ticket card only when there are any */}
      <div className={`grid grid-cols-2 gap-2 sm:gap-4 ${stats.totalCooperative > 0 ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3 sm:p-4 rounded-xl text-white">
          <div className="text-xs sm:text-sm opacity-90">Ümumi Biletlər</div>
          <div className="text-lg sm:text-2xl font-bold">{stats.totalTickets}</div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 p-3 sm:p-4 rounded-xl text-white">
          <div className="text-xs sm:text-sm opacity-90">Ümumi Gəlir</div>
          <div className="text-lg sm:text-2xl font-bold">{stats.totalRevenue}₼</div>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-3 sm:p-4 rounded-xl text-white">
          <div className="text-xs sm:text-sm opacity-90">Dəvətnamələr</div>
          <div className="text-lg sm:text-2xl font-bold">{stats.totalInvitations}</div>
        </div>
        {stats.totalCooperative > 0 && (
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-3 sm:p-4 rounded-xl text-white">
            <div className="text-xs sm:text-sm opacity-90">Əməkdaşlıq</div>
            <div className="text-lg sm:text-2xl font-bold">{stats.cooperativeAmount}₼</div>
            {/* Not part of the revenue, but commission is taken on it */}
            <div className="text-[11px] sm:text-xs opacity-90">{stats.totalCooperative} bilet</div>
          </div>
        )}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-3 sm:p-4 rounded-xl text-white">
          <div className="text-xs sm:text-sm opacity-90">Baxış Sayı</div>
          <div className="text-lg sm:text-2xl font-bold">{stats.viewers}</div>
        </div>
      </div>

      {/* Attendance: a table ticket counts all its seats */}
      {stats.expectedGuests > 0 && (
        <div className="bg-white px-3 py-3 sm:px-4 rounded-xl shadow flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm sm:text-base font-semibold">🚪 Daxil olanlar</span>
          <span className="text-sm sm:text-base">
            <strong className="text-lg">{stats.enteredGuests}</strong>
            <span className="text-gray-500"> / {stats.expectedGuests} nəfər</span>
          </span>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full"
              style={{ width: `${Math.min(100, Math.round((stats.enteredGuests / stats.expectedGuests) * 100))}%` }}
            />
          </div>
        </div>
      )}

      {/* The event's own question */}
      {customField && (
        <div className="bg-white p-3 sm:p-4 rounded-xl shadow">
          <h3 className="text-base sm:text-lg font-semibold mb-3">📝 {customField.label}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3 font-medium">Variant</th>
                  <th className="py-2 px-3 font-medium text-right">Biletlər</th>
                  <th className="py-2 px-3 font-medium text-right">Ödənilmiş</th>
                  <th className="py-2 pl-3 font-medium text-right">Gəlir</th>
                </tr>
              </thead>
              <tbody>
                {customField.rows?.map((row) => (
                  <tr key={row.value || "empty"} className="border-b last:border-0">
                    <td className="py-2 pr-3">
                      {row.value || <span className="text-gray-400">Seçilməyib</span>}
                    </td>
                    <td className="py-2 px-3 text-right">{row.tickets}</td>
                    <td className="py-2 px-3 text-right">{row.paid}</td>
                    <td className="py-2 pl-3 text-right">{row.revenue}₼</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Sales by Date - Main Chart */}
      <div className="bg-white p-1 sm:p-3 rounded-xl shadow relative">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 space-y-2 sm:space-y-0">
            <h3 className="text-base sm:text-lg font-semibold">📅 Bilet Satışları Tarixə Görə</h3>
            
            {/* Month Selector */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  const currentIndex = availableMonths.findIndex(m => m.value === selectedMonth);
                  if (currentIndex > 0) {
                    setSelectedMonth(availableMonths[currentIndex - 1].value);
                  }
                }}
                disabled={availableMonths.findIndex(m => m.value === selectedMonth) <= 0}
                className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Əvvəlki ay"
              >
                ←
              </button>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
              >
                {availableMonths.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  const currentIndex = availableMonths.findIndex(m => m.value === selectedMonth);
                  if (currentIndex < availableMonths.length - 1) {
                    setSelectedMonth(availableMonths[currentIndex + 1].value);
                  }
                }}
                disabled={availableMonths.findIndex(m => m.value === selectedMonth) >= availableMonths.length - 1}
                className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Növbəti ay"
              >
                →
              </button>
            </div>
          </div>
          
          <div className="mb-3 flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-2 sm:space-y-0">
            <div className="text-xs sm:text-sm text-gray-600">
              {availableMonths.find(m => m.value === selectedMonth)?.label || 'Ay seçin'} ayının satış məlumatları
            </div>
            <div className="flex space-x-3 text-xs sm:text-sm">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                <span>Biletlər</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span>Gəlir</span>
              </div>
            </div>
          </div>
          <div className="relative -mx-1 sm:-mx-3">
            {chartLoading && (
              <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10 rounded-lg">
                <div className="flex flex-col items-center space-y-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-sm text-gray-600">Məlumatlar yenilənir...</p>
                </div>
              </div>
            )}
            <ResponsiveContainer width="100%" height={300} className="sm:h-[350px]">
              <BarChart 
                data={stats.monthlySales || []} 
                margin={{ top: 20, right: 5, left: 0, bottom: 5 }}
              >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 8 }}
                tickLine={{ stroke: '#666' }}
                height={30}
              />
              <YAxis yAxisId="left" tick={{ fontSize: 8 }} width={20} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 8 }} width={30} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value, name) => [
                  name === 'tickets' ? `${value} bilet` : `${value}₼`, 
                  name === 'tickets' ? 'Satılan Biletlər' : 'Gəlir'
                ]}
                labelFormatter={(label) => `📅 ${label}`}
              />
              <Bar 
                yAxisId="left"
                dataKey="tickets" 
                fill="#3B82F6" 
                radius={[4, 4, 0, 0]}
                name="tickets"
                className={chartLoading ? "opacity-50" : ""}
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="revenue" 
                stroke="#10B981" 
                strokeWidth={3}
                dot={{ fill: '#10B981', strokeWidth: 2, r: 5 }}
                name="revenue"
                className={chartLoading ? "opacity-50" : ""}
              />
            </BarChart>
          </ResponsiveContainer>
          </div>
        </div>
    </div>
  );
};

export default TicketStatsComponent;
